import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, Info, Monitor } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { apiMessage, pruneBody, useRequireScope } from '../helpers';
import { planLimitRefusal, type PlanLimitRefusal } from '../planLimits';
import PlanLimitNotice from './PlanLimitNotice';
import { DEVICE_TYPES, type Branch, type DeviceType } from '../types';
import { Stepper } from '@/components/ui/stepper';

/**
 * The server no longer returns a raw `fingerprint` for a device that has not
 * been paired, and it now says which of the three pairing states a row is in.
 * Declared here rather than in `../types` because the neighbouring Settings
 * panels share that file and none of them reads these fields.
 */
export type PairingStatus = 'PENDING' | 'EXPIRED' | 'PAIRED';

export interface PairedDevice {
  id: string;
  name: string;
  /** Null until a device claims its code — see `pairing.status`. */
  fingerprint: string | null;
  type: DeviceType;
  branchId: string | null;
  isActive: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  branch?: { id: string; name: string; code: string } | null;
  user?: { id: string; firstName: string; lastName: string | null } | null;
  pairing?: { status: PairingStatus; expiresAt: string | null };
}

/** What `POST /devices/pairing` and `POST /devices/:id/pairing` hand back. */
export interface IssuedPairing {
  device: PairedDevice;
  pairingCode: string;
  expiresAt: string;
}

interface Props {
  open: boolean;
  device: PairedDevice | null;
  onClose: () => void;
  onSaved: () => void;
}

/** The wizard's three steps, in the order the Device Setup frame draws them. */
const CREATE_STEPS = ['type', 'configuration', 'pair'] as const;
type CreateStep = (typeof CREATE_STEPS)[number];

/**
 * Add a device, or edit one that already exists (§13).
 *
 * **Adding no longer asks for a fingerprint, because it never could.** The
 * fingerprint is minted by the app on the device (`getDeviceFingerprint()` — the
 * Android ID or the iOS vendor id) and only exists once that app has run, so the
 * old form asked an admin to type a value they had no way of obtaining, for a
 * device that may still have been in its box. Adding creates the row from what
 * an admin genuinely knows — name, type, branch — and issues a pairing code that
 * the device redeems with its own identifier.
 *
 * Creating now walks the Device Setup frame's three steps — type, configuration,
 * assign — with **one substitution that is the whole point of the screen**: the
 * frame's final step is "Assign", and there is nothing to assign. `Device.userId`
 * is bound by `pair/claim` when a person signs in on the device, never by an
 * admin, and `branchId` is already step 2. What the flow actually delivers at
 * the end is the pairing code, which exists exactly once and is never
 * recoverable — the server stores only its HMAC. So step 3 is the code. A wizard
 * built literally from the frame would issue that code and throw it away.
 *
 * Five of the frame's step-2 fields are not built: IP address, connected
 * printer, cash drawer, receipt format and auto-lock timeout. None of them is on
 * `Device` and none is on `CreatePairingDto`, so with `forbidNonWhitelisted`
 * each one would 400 the registration and take the name and branch down with it.
 * The IP address is the same mistake as the old fingerprint field one layer up:
 * a value the device knows and the admin does not.
 *
 * The frame's `Connection test` / `Connected` / `Last ping: < 1ms` card is not
 * built either. The only liveness datum in the product is `Device.lastSeenAt`,
 * written device→server by a heartbeat; there is no `online` field, no freshness
 * threshold defined anywhere in either repo, and no admin-initiated probe. The
 * honest version of that card is the `Last seen` row in Diagnostics, which is
 * already here.
 *
 * On the edit path there is no wizard: a registered device is three fields and
 * its read-only diagnostics, as before.
 */
export default function DeviceFormDialog({ open, device, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const branchesQuery = useApi<unknown>(open ? '/branches' : null, { limit: 200 });
  const branches = rowsOf<Branch>(branchesQuery.data).rows;
  const inScope = useRequireScope();

  const [step, setStep] = useState<CreateStep>('type');
  const [name, setName] = useState('');
  const [type, setType] = useState<DeviceType>('POS');
  const [branchId, setBranchId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState<IssuedPairing | null>(null);
  /** The plan ceiling this venue just walked into, if that is what refused the pairing. */
  const [planLimit, setPlanLimit] = useState<PlanLimitRefusal | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setIssued(null);
    setStep('type');
    setName(device?.name ?? '');
    setType(device?.type ?? 'POS');
    setBranchId(device?.branchId ?? '');
    setPlanLimit(null);
  }, [open, device]);

  const status = device?.pairing?.status ?? 'PAIRED';

  /**
   * `POST /devices/pairing` carries `@RequireScope(BRANCH, 'branchId')`, so a
   * branch-scoped admin may only issue codes inside their own branch — holding
   * `devices.register` is not the whole precondition. Editing (`PATCH
   * /devices/:id`) declares no scope, so this only narrows the create path.
   */
  const branchBlocked = (id: string) => !device && !inScope('BRANCH', id);
  const branchOutOfScope = Boolean(branchId) && branchBlocked(branchId);

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t('settings.validation.required');
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    setPlanLimit(null);
    try {
      const body = pruneBody({ name: name.trim(), type, branchId });
      if (device) {
        await api.patch(`/devices/${device.id}`, body);
        toast.success(t('settings.devices.updated'));
        onSaved();
        onClose();
      } else {
        // Creates the row in pending-pairing state and returns a one-time code.
        // The row is real and listable immediately; it simply cannot sign anyone
        // in until a device claims the code. The list is refreshed here, not on
        // close, so the new pending row is behind the dialog while the code is
        // still on screen.
        const { data } = await api.post<IssuedPairing>('/devices/pairing', body);
        onSaved();
        setIssued(data);
        setStep('pair');
      }
    } catch (error) {
      // A device is metered by the plan too, and it is checked at ISSUE rather than at claim —
      // so this is the moment the ceiling is announced, with a panel that names the way out
      // rather than the server's English in a red toast.
      const refusal = planLimitRefusal(error);
      setPlanLimit(refusal);
      if (!refusal) toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const stepIndex = CREATE_STEPS.indexOf(step);
  const stepLabels = {
    type: t('settings.devices.stepType'),
    configuration: t('settings.devices.stepConfiguration'),
    pair: t('settings.devices.stepPair'),
  } as const;

  const footer = device ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={saving}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => void submit()} disabled={saving}>
        {saving ? t('settings.common.saving') : t('common.save')}
      </Button>
    </>
  ) : step === 'pair' ? (
    <Button onClick={onClose}>{t('common.close')}</Button>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={saving}>
        {t('common.cancel')}
      </Button>
      {step === 'configuration' && (
        <Button variant="outline" onClick={() => setStep('type')} disabled={saving}>
          {t('common.back')}
        </Button>
      )}
      {step === 'type' ? (
        <Button onClick={() => setStep('configuration')}>{t('common.next')}</Button>
      ) : (
        <Button
          onClick={() => void submit()}
          disabled={saving || branchOutOfScope}
          title={branchOutOfScope ? t('settings.devices.branchOutOfScope') : undefined}
        >
          {saving ? t('settings.common.saving') : t('settings.devices.pairingCode')}
        </Button>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={device ? t('settings.devices.editTitle') : t('settings.devices.addTitle')}
      description={device ? t('settings.devices.formHint') : t('settings.devices.addHint')}
      footer={footer}
    >
      <div className="space-y-4">
        {planLimit && <PlanLimitNotice refusal={planLimit} />}

        {!device && (
          <Stepper
            current={stepIndex}
            steps={CREATE_STEPS.map((id) => ({ id, label: stepLabels[id] }))}
          />
        )}

        {(device || step === 'type') && (
          <Field label={t('settings.devices.type')}>
            <Select value={type} onChange={(e) => setType(e.target.value as DeviceType)}>
              {DEVICE_TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`settings.devices.types.${option}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!device && step === 'type' && (
          <p className="rounded-lg bg-subtle p-3 text-xs text-ink-muted">
            <span className="mb-1 flex items-center gap-1.5 font-medium">
              <Icon as={Info} size="sm" />
              {t('settings.devices.howItWorks')}
            </span>
            {t('settings.devices.howItWorksBody')}
          </p>
        )}

        {(device || step === 'configuration') && (
          <>
            <Field label={t('settings.devices.name')} required error={errors.name}>
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            <Field label={t('settings.devices.branch')}>
              <Select
                value={branchId}
                disabled={branchesQuery.loading}
                onChange={(e) => setBranchId(e.target.value)}
              >
                <option value="">
                  {branchesQuery.loading ? t('common.loading') : t('settings.devices.noBranch')}
                </option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id} disabled={branchBlocked(branch.id)}>
                    {branch.name}
                    {branchBlocked(branch.id) ? ` — ${t('settings.devices.branchOutOfScope')}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            {branchOutOfScope && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {t('settings.devices.branchOutOfScope')}
              </p>
            )}
          </>
        )}

        {!device && step === 'configuration' && (
          <DevicePreview name={name} type={type} />
        )}

        {!device && step === 'pair' && issued && <PairingCodePanel issued={issued} />}

        {device && (
          <section className="space-y-2 rounded-lg border p-3">
            <h3 className="text-xs font-semibold uppercase text-ink-muted">
              {t('settings.devices.diagnostics')}
            </h3>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">{t('settings.devices.fingerprint')}</dt>
                <dd
                  className="max-w-[60%] truncate font-mono text-xs"
                  dir="ltr"
                  title={device.fingerprint ?? undefined}
                >
                  {device.fingerprint ?? t('settings.devices.notPairedYet')}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">{t('settings.devices.lastSeen')}</dt>
                <dd className="text-xs">
                  {device.lastSeenAt
                    ? formatDateTime(device.lastSeenAt, i18n.language)
                    : t('settings.common.never')}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-ink-muted">{t('settings.devices.fingerprintExplainer')}</p>
            {status !== 'PAIRED' && (
              <p className="text-xs text-ink-muted">{t('settings.devices.repairHint')}</p>
            )}
          </section>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The frame's `Device preview` card, minus the parts of it that were reading
 * fields the product does not have: it echoes the name and the type, and not an
 * IP address.
 */
function DevicePreview({ name, type }: { name: string; type: DeviceType }) {
  const { t } = useTranslation();
  return (
    <div className="grid place-items-center gap-2 rounded-lg border border-dashed p-4 text-center">
      <span className="flex h-12 w-16 items-center justify-center rounded-md border text-[11px] font-medium text-ink-muted">
        <Icon as={Monitor} size="lg" className="text-accent" aria-hidden />
      </span>
      <p className="text-sm font-medium">
        {name.trim() || t('settings.devices.unnamedPreview')}
      </p>
      <p className="text-xs text-ink-muted">{t(`settings.devices.types.${type}`)}</p>
    </div>
  );
}

/**
 * The one moment the plaintext pairing code exists outside the device that will
 * claim it.
 *
 * The server stores only an HMAC of the code, so this cannot be re-opened later
 * to read it back — that is deliberate, and it is why the panel offers "new
 * code" rather than "show code". The code is rendered large and monospaced
 * because its whole job is to be read off one screen and typed into another, and
 * the countdown is live so nobody starts walking to a tablet with forty seconds
 * left.
 *
 * There is no QR: neither the dashboard nor the staff apps carry a QR
 * encoder/scanner today, so it would mean a new dependency on both sides for a
 * code short enough to type in six seconds. Noted rather than smuggled in.
 *
 * Shared by the create wizard's last step and by the panel's re-pair path, so a
 * code looks and behaves the same however it was issued.
 */
export function PairingCodePanel({ issued }: { issued: IssuedPairing }) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setCopied(false);
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [issued]);

  const remaining = useMemo(
    () => Math.max(0, new Date(issued.expiresAt).getTime() - now),
    [issued, now],
  );

  const expired = remaining <= 0;
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.pairingCode);
      setCopied(true);
      toast.success(t('settings.devices.pairingCodeCopied'));
    } catch {
      toast.error(t('settings.errors.actionFailed'));
    }
  };

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-ink-muted">
        {t('settings.devices.pairingCodeIntro', { minutes: 15 })}
      </p>

      {/* `dir="ltr"` and digits only, so the tracking here is never applied to
          Arabic letterforms — it is spacing eight numerals for reading aloud. */}
      <p
        className="select-all rounded-xl border-2 border-dashed py-6 font-mono text-4xl font-bold tracking-[0.2em]"
        dir="ltr"
        aria-label={t('settings.devices.pairingCode')}
      >
        {issued.pairingCode}
      </p>

      <Button variant="outline" onClick={() => void copy()} disabled={expired}>
        <Icon as={copied ? Check : Copy} />
        {t('settings.devices.pairingCodeCopy')}
      </Button>

      <p className={expired ? 'text-sm text-red-600 dark:text-red-400' : 'text-sm text-ink-muted'}>
        {expired
          ? t('settings.devices.pairingCodeExpired')
          : `${minutes}:${String(seconds).padStart(2, '0')} · ${t(
              'settings.devices.pairingCodeExpiresAt',
              { time: formatDateTime(issued.expiresAt, i18n.language) },
            )}`}
      </p>

      <p className="text-start text-xs text-ink-muted">
        {t('settings.devices.howItWorksBody')}
      </p>
      {/*
        A device already registered to another venue does not fail here: it is
        offered a transfer at the tablet (`ClaimPairingDto.transfer`), which
        retires the old registration and writes the move on both sides. There is
        no admin-side control for it and there should not be — the person holding
        the device is the one who can confirm it is the right device.
      */}
      <p className="text-start text-xs text-ink-muted">
        {t('settings.devices.transferHint')}
      </p>
    </div>
  );
}

/** The re-pair path's wrapper: the same code panel, in its own dialog. */
export function PairingCodeDialog({
  issued,
  onClose,
}: {
  issued: IssuedPairing | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!issued) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('settings.devices.pairingCodeTitle', { name: issued.device.name })}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <PairingCodePanel issued={issued} />
    </Dialog>
  );
}
